// src/components/JarvislyComponents/EnrichedEntityModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback } from 'react';
import {
  Flex,
  Typography,
  Card,
  Select,
  Spin,
  Alert,
  Button,
  Statistic,
  Tooltip,
  theme,
} from 'antd';
import {
  WalletOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ShoppingCartOutlined,
  StarFilled,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import dataSearchService from 'services/dataSearchService';
import marketplaceService from 'services/marketplaceService';
import axiosService from 'services/axiosService';
import authService from 'services/authService';
import { API } from 'constants/Api';
import useZustand from 'hooks/useZustand';
import { openModal } from 'components/core/GenericModal';
import InfoModal from 'modules/app-views/dataSearch/pages/Search/modals/InfoModal';

const { Text, Title } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// Search engines configuration (same as dataSearch module)
const SEARCH_ENGINES = [
  {
    value: 'hod',
    label: 'HOD',
    provider: 'jarvisly',
    online: true,
    services: ['cnpj'],
    stars: 2,
  },
  {
    value: 'chokmah',
    label: 'Chokmah',
    provider: 'iwebInfo',
    online: false,
    services: ['cnpj', 'cpf'],
    stars: 3,
  },
  {
    value: 'metatron',
    label: 'Metatron',
    provider: 'vigidata',
    online: false,
    services: ['cpf', 'cnpj', 'name', 'phone', 'email'],
    stars: 5,
  },
];

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const EnrichedEntityStepContent = ({
  documentValue,
  documentType,
  selectedEngine,
  onEngineChange,
  creditsBalance,
  isSearching,
  setIsSearching,
  searchResult,
  setSearchResult,
  entityId,
  searchActionRef,
  onSuccess,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const setSettingsDrawerOpen = useZustand('theme', 'setSettingsDrawerOpen');
  const [enrichmentPrices, setEnrichmentPrices] = useState(null);
  const [apiSearchEngines, setApiSearchEngines] = useState(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch enrichment prices on mount
  useEffect(() => {
    fetchEnrichmentPrices();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchEnrichmentPrices = async () => {
    setIsLoadingPrices(true);
    try {
      const [response] = await axiosService.get({
        url: API.MIGRATION.PRICES,
        returnAsObject: false,
      });
      const data = Array.isArray(response) ? response[0] : response;
      if (data?.prices) {
        setEnrichmentPrices(data.prices);
      }
      if (data?.searchEngines) {
        setApiSearchEngines(data.searchEngines);
      }
    } catch (error) {
      console.error('[EnrichedEntityModal] Error fetching enrichment prices:', error);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleSearch = async () => {
    if (!documentValue) {
      appService.message({ flag: 'w', text: 'document_value_required' });
      return false;
    }

    // Check credits
    const requiredCredits = getEnginePrice(selectedEngine);
    if (creditsBalance < requiredCredits) {
      appService.notification({
        flag: 'warning',
        title: 'insufficient_credits',
        description: translateX('insufficient_credits_description', {
          required: requiredCredits,
          available: creditsBalance,
        }),
      });
      return false;
    }

    setIsSearching(true);
    setSearchResult(null);

    try {
      const result = await dataSearchService.performSearch({
        engine: selectedEngine,
        serviceType: documentType,
        searchValue: documentValue,
      });

      setSearchResult(result);

      if (result?.success && result?.data?.success) {
        const resultData = result.data;
        const dataSearchId = result.dataSearchId;

        // Extract entity data (cnpjData or cpfData) and enrichment
        const rawEntityData = resultData.cnpjData || resultData.cpfData || {};
        const enrichmentData = resultData.enrichment || resultData._enrichment || {};

        // Merge enrichment into entity data for buildEntityUpdatePayload
        const entityDataWithEnrichment = {
          ...rawEntityData,
          _enrichment: enrichmentData,
        };

        // 1. Build update payload from the new search data
        const updatePayload = dataSearchService.buildEntityUpdatePayload(entityDataWithEnrichment);

        // 2. Update the entity with the new enriched data
        if (entityId && Object.keys(updatePayload).length > 0) {
          const baseUrl = API.ENTITIES.URL.replace('/:_id?', '');
          const url = `${baseUrl}/${entityId}`;
          await axiosService.put({ url, data: updatePayload });
        }

        // 3. Link the new dataSearch to the entity
        if (dataSearchId && entityId) {
          await dataSearchService.linkToEntity({
            _id: dataSearchId,
            entityId: entityId,
            syncStatus: 'synced',
          });
        }

        // Refresh auth to update credits balance
        await authService.signInRefresh();

        appService.notification({
          flag: 'success',
          title: 'search_completed',
          description: translateX('enrichment_data_refreshed'),
        });

        // Show credits debit notification
        const creditsDebit = result._creditsDebit;
        if (creditsDebit?.debited > 0) {
          appService.notification({
            flag: 'info',
            title: 'credits_debited',
            description: translateX('credits_debited_description', {
              debited: creditsDebit.debited,
              operation: translateX('data_search'),
              balance: creditsDebit.balanceAfter,
            }),
          });
        }

        // Notify parent of success (called before modal closes)
        if (onSuccess) onSuccess(result);

        return true;
      } else {
        appService.notification({
          flag: 'error',
          title: 'search_failed',
          description: result?.error || result?.data?.error || translateX('search_failed'),
        });
        return false;
      }
    } catch (error) {
      console.error('[EnrichedEntityModal] Search error:', error);
      setSearchResult({ success: false, error: error.message });
      appService.notification({
        flag: 'error',
        title: 'search_failed',
        description: error.message || translateX('search_failed'),
      });
      return false;
    } finally {
      setIsSearching(false);
    }
  };

  // Expose search action to GenericModal's footer button via ref
  if (searchActionRef) searchActionRef.current = handleSearch;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Show ALL engines, mark as disabled if they don't support the documentType
  const availableEngines = SEARCH_ENGINES.map(engine => ({
    ...engine,
    supportsType: engine.services.includes(documentType),
  }));

  // Merge local engines with API data (online status, prices)
  // API engines use .id (not .value), and prices are per-service: { cnpj: { priceBRL }, cpf: { priceBRL } }
  const mergedEngines = availableEngines.map(localEngine => {
    const apiEngine = apiSearchEngines?.find(e => e.id === localEngine.value);
    const isOnline = apiEngine?.online ?? localEngine.online;

    // Get price for the current documentType from engine's API prices
    const apiPrices = apiEngine?.prices;
    const servicePrice = apiPrices?.[documentType];
    const priceBRL = servicePrice?.priceBRL || 0;
    // Convert BRL to credits (1 credit = R$ 0.10)
    const priceInCredits = priceBRL > 0 ? Math.ceil(priceBRL * 10) : 0;

    return {
      ...localEngine,
      online: isOnline,
      price: priceInCredits,
      disabled: !localEngine.supportsType || !isOnline,
    };
  });

  // Auto-select first available engine if current selection is disabled
  useEffect(() => {
    const currentEngine = mergedEngines.find(e => e.value === selectedEngine);
    if (currentEngine?.disabled) {
      const firstAvailable = mergedEngines.find(e => !e.disabled);
      if (firstAvailable) onEngineChange(firstAvailable.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType, selectedEngine, isLoadingPrices]);

  // Build Select options for engine dropdown
  const engineOptions = mergedEngines.map(engine => ({
    value: engine.value,
    label: engine.label,
    disabled: engine.disabled,
  }));

  // Render engine option in dropdown (with stars and status)
  function renderEngineOption(option) {
    const engine = mergedEngines.find(e => e.value === option.data.value);
    if (!engine) return <span>{option.data.label}</span>;

    return (
      <Flex align="center" justify="space-between" style={{ width: '100%' }}>
        <Flex align="center" gap={8}>
          <span>{engine.label}</span>
          {!engine.online && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({translateX('offline')})
            </Text>
          )}
          {engine.online && !engine.supportsType && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({translateX('service_not_available')})
            </Text>
          )}
        </Flex>
        <Flex gap={2}>
          {Array.from({ length: engine.stars }, (_, i) => (
            <StarFilled key={i} style={{ fontSize: 11, color: token.colorWarning }} />
          ))}
        </Flex>
      </Flex>
    );
  }

  // Render selected engine label (with stars)
  function renderEngineLabel({ value, label }) {
    const engine = mergedEngines.find(e => e.value === value);
    if (!engine) return <span>{label}</span>;

    return (
      <Flex align="center" justify="space-between" style={{ width: '100%', paddingRight: 8 }}>
        <span>{engine.label}</span>
        <Flex gap={2}>
          {Array.from({ length: engine.stars }, (_, i) => (
            <StarFilled key={i} style={{ fontSize: 11, color: token.colorWarning }} />
          ))}
        </Flex>
      </Flex>
    );
  }

  // Get price for selected engine (already in credits from mergedEngines)
  function getEnginePrice(engineValue) {
    const engine = mergedEngines.find(e => e.value === engineValue);
    return engine?.price || 0;
  }

  const selectedEnginePrice = getEnginePrice(selectedEngine);
  const hasSufficientCredits = creditsBalance >= selectedEnginePrice;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (isLoadingPrices) {
    return (
      <Flex justify="center" align="center" style={{ padding: 48 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div style={{ paddingTop: 8 }}>
      {/* Document info */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={12}>
          <Text type="secondary">{translateX(documentType.toUpperCase())}:</Text>
          <Text strong style={{ fontSize: 16 }}>
            {formatDocument(documentValue, documentType)}
          </Text>
        </Flex>
      </Card>

      {/* Credits balance */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={8}>
            <WalletOutlined style={{ fontSize: 18, color: token.colorPrimary }} />
            <Text>{translateX('credits_balance')}</Text>
          </Flex>
          <Flex align="center" gap={8}>
            <Statistic
              value={creditsBalance}
              precision={0}
              styles={{
                content: {
                  fontSize: 18,
                  fontWeight: 600,
                  color: creditsBalance > 0 ? token.colorSuccess : token.colorError,
                },
              }}
            />
            <Tooltip title={translateX('add_more_credits')}>
              <Button
                type="text"
                size="small"
                icon={<ShoppingCartOutlined />}
                onClick={() => setSettingsDrawerOpen(true)}
              />
            </Tooltip>
          </Flex>
        </Flex>
      </Card>

      {/* Engine selection */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translateX('select_search_engine')}
      </Text>

      <Select
        className="engine-select-stars"
        value={selectedEngine}
        onChange={onEngineChange}
        options={engineOptions}
        optionRender={renderEngineOption}
        labelRender={renderEngineLabel}
        disabled={isSearching}
        style={{ width: '100%', height: 40 }}
      />
      <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
        {translateX('data_search_cost_credits', { credits: selectedEnginePrice })}
      </Text>

      {/* Learn more link */}
      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <a onClick={() => openModal(InfoModal)} style={{ fontSize: 13, cursor: 'pointer' }}>
          {translateX('learn_more')}
        </a>
      </div>

      {/* Insufficient credits warning */}
      {!hasSufficientCredits && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          title={
            <span>
              {translateX('your_balance_is_plain')}{' '}
              <strong>{creditsBalance}</strong> {translateX('credits')}.{' '}
              <a onClick={() => setSettingsDrawerOpen(true)} style={{ cursor: 'pointer' }}>
                {translateX('click_to_acquire_credits')}
              </a>
            </span>
          }
        />
      )}

      {/* Search result */}
      {searchResult && (
        <Alert
          type={searchResult.success && searchResult.data?.success ? 'success' : 'error'}
          showIcon
          icon={
            searchResult.success && searchResult.data?.success ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
          style={{ marginTop: 16 }}
          title={
            searchResult.success && searchResult.data?.success
              ? translateX('enrichment_data_refreshed')
              : translateX('search_failed')
          }
          description={
            searchResult.success && searchResult.data?.success
              ? translateX('entity_data_updated')
              : searchResult.error || searchResult.data?.error || translateX('try_again_later')
          }
        />
      )}
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const enrichedEntityStep = ({
  entityId,
  documentValue,
  documentType,
  selectedEngine,
  onEngineChange,
  creditsBalance,
  isSearching,
  setIsSearching,
  searchResult,
  setSearchResult,
  searchActionRef,
  onSuccess,
}) => ({
  stepTitle: 'refresh_enrichment',
  stepTooltip: 'refresh_enrichment_tooltip',
  nextButtonText: 'refresh_data',
  nextButtonIcon: <SyncOutlined />,
  onNext: async () => {
    if (!searchActionRef?.current) return false;
    return await searchActionRef.current();
  },
  content: () => (
    <EnrichedEntityStepContent
      entityId={entityId}
      documentValue={documentValue}
      documentType={documentType}
      selectedEngine={selectedEngine}
      onEngineChange={onEngineChange}
      creditsBalance={creditsBalance}
      isSearching={isSearching}
      setIsSearching={setIsSearching}
      searchResult={searchResult}
      setSearchResult={setSearchResult}
      searchActionRef={searchActionRef}
      onSuccess={onSuccess}
    />
  ),
  validationFields: [],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default enrichedEntityStep;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function formatDocument(value, type) {
  if (!value) return '';
  const strValue = typeof value === 'string' ? value : String(value);
  const cleaned = strValue.replace(/\D/g, '');

  if (type === 'cnpj' && cleaned.length === 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  if (type === 'cpf' && cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return strValue;
}
